import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { WorkshopMultiDetailDTO, WorkshopPreviewDTO, WorkshopSessionBody, WorkshopSessionDTO } from "../../../../services/v2/workshops.service";
import useEsTelefono from "../../../../utils/esTelefono";
import S from "./strings";
import { Opciones, errorText } from "./comun";
import { FechaDMY } from "../../../../components/fechas/camposFecha";
import DiasEditor, { DiaDraft, HoraSelect, diaValido, horasEntre } from "./dias";
import { QueVaAPasar, Ventana, diaSemanaFecha, rangoHoras } from "./piezasTaller";

/*
	«Días» of the workshop window (Módulos Claros, 2026-11-01): the compact version of the old «Sesiones» box. Date, time in
	am/pm, classroom and teacher; with «Editar Días» each day can be edited or deleted and new days added. Deleting a day goes
	through «Qué Va a Pasar» (it erases that day's attendance).
*/

const pie = (onCancel: () => void, busy: boolean, ok: React.ReactNode) => <>
	<button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={busy}>{S.cancelar}</button>
	<span style={{ flex: 1 }} />
	{ok}
</>;

/** Edit one day. */
const DiaForm: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; sesion: WorkshopSessionDTO; opciones: Opciones;
	onClose: () => void; onSaved: (t: WorkshopMultiDetailDTO) => void }> = ({ campusId, taller, sesion, opciones, onClose, onSaved }) => {
	const http = HttpManager.getInstance();
	const [f, setF] = useState({ date: sesion.date, startTime: sesion.startTime, endTime: sesion.endTime || "", classroomId: sesion.classroomId || 0, mentorId: sesion.mentorId || 0, notes: sesion.notes || "" });
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const set = (k: keyof typeof f, v: any) => setF((x) => ({ ...x, [k]: v }));
	const valido = /^\d{4}-\d{2}-\d{2}$/.test(f.date) && f.startTime !== "" && horasEntre(f.startTime, f.endTime) > 0;
	const aulas = opciones.aulas.map((a) => ({ id: a.id, name: a.buildingName ? `${a.name}, ${a.buildingName}` : a.name }));
	if (f.classroomId && !aulas.some((a) => a.id === f.classroomId)) aulas.unshift({ id: f.classroomId, name: sesion.classroomName || taller.classroomName || String(f.classroomId) });
	const maestras = opciones.maestras.map((m) => ({ id: m.id, name: m.name }));
	if (f.mentorId && !maestras.some((m) => m.id === f.mentorId)) maestras.unshift({ id: f.mentorId, name: sesion.mentorName || String(f.mentorId) });

	const guardar = () => {
		const body: WorkshopSessionBody = { date: f.date, startTime: f.startTime, endTime: f.endTime || null, classroomId: f.classroomId || null, mentorId: f.mentorId || null, notes: f.notes.trim() || null };
		setBusy(true); setError(null);
		http.workshopsService.updateSession(campusId, sesion.id, body)
			.then((r) => onSaved(r.data))
			.catch((e) => { setError(errorText(e)); setBusy(false); });
	};

	return (
		<Ventana titulo={S.editarDia} onClose={onClose} busy={busy} pie={pie(onClose, busy, <button type="button" className="btn btn-primary btn-sm" onClick={guardar} disabled={busy || !valido}>{S.guardar}</button>)}>
			<div className="wau-fila wte-fila">
				<div className="wau-campo" style={{ flex: "1 1 160px" }}><span className="wau-lab">{S.fecha}</span>
					<FechaDMY className="wau-entrada wau-num" value={f.date} onChange={(v) => set("date", v)} /></div>
				<div className="wau-campo" style={{ flex: "1 1 140px" }}><span className="wau-lab">{S.entrada}</span>
					<HoraSelect value={f.startTime} onChange={(v) => set("startTime", v)} label={S.entrada} /></div>
				<div className="wau-campo" style={{ flex: "1 1 140px" }}><span className="wau-lab">{S.salida}</span>
					<HoraSelect value={f.endTime} onChange={(v) => set("endTime", v)} label={S.salida} /></div>
			</div>
			<div className="wau-fila wte-fila">
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.aula}</span>
					<select className="wau-entrada" value={f.classroomId} onChange={(e) => set("classroomId", Number(e.target.value))}>
						<option value={0}>{S.ninguna}</option>
						{aulas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
					</select></label>
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.maestra}</span>
					<select className="wau-entrada" value={f.mentorId} onChange={(e) => set("mentorId", Number(e.target.value))}>
						<option value={0}>{S.ninguna}</option>
						{maestras.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
					</select></label>
			</div>
			<label className="wau-campo"><span className="wau-lab">{S.notas}</span>
				<textarea className="wau-entrada" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></label>
			{error ? <div className="waAviso" role="alert">{error}</div> : null}
		</Ventana>
	);
};

/** Add several days at once: same calendar as when creating; new days copy the last one. */
export const AgregarDias: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; opciones: Opciones; onClose: () => void;
	onSaved: (t: WorkshopMultiDetailDTO, n: number) => void }> = ({ campusId, taller, opciones, onClose, onSaved }) => {
	const http = HttpManager.getInstance();
	const ss = taller.sessions || [];
	const previa = ss[ss.length - 1] || null;
	const [dias, setDias] = useState<DiaDraft[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const valido = dias.length > 0 && dias.every(diaValido);
	const guardar = () => {
		setBusy(true); setError(null);
		http.workshopsService.addSessions(campusId, taller.id, dias.map((d) => ({ date: d.date, startTime: d.startTime, endTime: d.endTime || null, classroomId: d.classroomId || null, mentorId: d.mentorId || null })))
			.then((r) => onSaved(r.data, dias.length))
			.catch((e) => { setError(errorText(e)); setBusy(false); });
	};
	return (
		<Ventana titulo={S.agregarDias} onClose={onClose} busy={busy} size="xl"
			pie={pie(onClose, busy, <button type="button" className="btn btn-primary btn-sm" onClick={guardar} disabled={busy || !valido}>{S.agregarDias}</button>)}>
			<DiasEditor value={dias} onChange={setDias} opciones={opciones} ocupados={ss.map((x) => x.date)}
				base={previa ? { date: previa.date, startTime: previa.startTime, endTime: previa.endTime || "", classroomId: previa.classroomId || 0, mentorId: previa.mentorId || 0 }
					: { date: String(taller.startsAt || "").substring(0, 10) || undefined }} />
			{error ? <div className="waAviso" role="alert">{error}</div> : null}
		</Ventana>
	);
};

/** The «Días» block of the window. */
export const DiasTaller: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; opciones: Opciones; editando: boolean;
	onChange: (t: WorkshopMultiDetailDTO) => void; notify: (text: string, ok?: boolean) => void }> = ({ campusId, taller, opciones, editando, onChange, notify }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [form, setForm] = useState<WorkshopSessionDTO | null>(null);
	const [agregar, setAgregar] = useState<boolean>(false);
	const [vista, setVista] = useState<{ sesion: WorkshopSessionDTO; v: WorkshopPreviewDTO } | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const sesiones = taller.sessions || [];

	const pedirBorrar = (x: WorkshopSessionDTO) => {
		setBusy(true); setError(null);
		http.workshopsService.preview(campusId, taller.id, "borrarDia", { sessionId: x.id })
			.then((r) => setVista({ sesion: x, v: r.data }))
			.catch((e) => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const borrar = () => {
		if (!vista) return;
		setBusy(true); setError(null);
		http.workshopsService.deleteSession(campusId, vista.sesion.id)
			.then((r) => { onChange(r.data); setVista(null); notify(S.diaBorrado); })
			.catch((e) => setError(errorText(e)))
			.finally(() => setBusy(false));
	};
	const lugar = (x: WorkshopSessionDTO) => [x.classroomName, x.mentorName].filter(Boolean).join(", ");
	const acciones = (x: WorkshopSessionDTO) => editando ? (
		<span style={{ display: "inline-flex", gap: 4 }}>
			<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setForm(x)}>{S.editar}</button>
			<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy || sesiones.length <= 1} onClick={() => pedirBorrar(x)}>{S.borrar}</button>
		</span>
	) : null;

	return (
		<div className="wtlSeccion">
			<div className="wtlSecCab">
				<h6>{S.dias} <span className="wtlSub" style={{ display: "inline" }}>{sesiones.length}</span></h6>
				{editando ? <button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={() => setAgregar(true)}>{S.agregarDias}</button> : null}
			</div>
			{sesiones.length === 0 ? <div className="wtlSub">{S.sinDias}</div> : movil ? (
				<div>
					{sesiones.map((x) => (
						<div key={x.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--wa-filo)", alignItems: "center" }}>
							<div style={{ minWidth: 0 }}><b className="wau-num">{diaSemanaFecha(x.date)}</b> <span className="wau-num">{rangoHoras(x.startTime, x.endTime)}</span>
								{lugar(x) ? <div className="wtlSub">{lugar(x)}</div> : null}</div>
							{acciones(x)}
						</div>
					))}
				</div>
			) : (
				<table className="table table-sm wtlTabla">
					<thead><tr><th>{S.fecha}</th><th>{S.horario}</th><th>{S.aula}</th><th>{S.maestra}</th>{editando ? <th /> : null}</tr></thead>
					<tbody>
						{sesiones.map((x) => (
							<tr key={x.id}>
								<td className="wau-num" style={{ whiteSpace: "nowrap" }}>{diaSemanaFecha(x.date)}</td>
								<td className="wau-num" style={{ whiteSpace: "nowrap" }}>{rangoHoras(x.startTime, x.endTime)}{x.notes ? <div className="wtlSub">{x.notes}</div> : null}</td>
								<td>{x.classroomName || "—"}</td>
								<td>{x.mentorName || "—"}</td>
								{editando ? <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{acciones(x)}</td> : null}
							</tr>
						))}
					</tbody>
				</table>
			)}
			{form ? <DiaForm campusId={campusId} taller={taller} opciones={opciones} sesion={form} onClose={() => setForm(null)}
				onSaved={(t) => { onChange(t); setForm(null); notify(S.diaGuardado); }} /> : null}
			{agregar ? <AgregarDias campusId={campusId} taller={taller} opciones={opciones} onClose={() => setAgregar(false)}
				onSaved={(t, n) => { onChange(t); setAgregar(false); notify(S.diasAgregados(n)); }} /> : null}
			{vista ? <QueVaAPasar vista={vista.v} boton={S.borrarDia} busy={busy} error={error} onCancel={() => { setVista(null); setError(null); }} onConfirm={borrar} /> : null}
		</div>
	);
};
